import PageShell from "@/components/PageShell";
import RichHtml from "@/components/ui/RichHtml";
import { resolveFontSize } from "@/utils/headingSize";

/**
 * CustomPageBody - renders a Custom Static Page's title + rich content.
 * Extracted from CustomStaticPage.jsx so it can also be reused by
 * CmsPageOverride (hardcoded routes that fall back to a Custom Page).
 */
export default function CustomPageBody({ page }) {
  const ts = page.title_style || {};
  // Empty title_style means "follow the Site Styles defaults" — we
  // apply only the inline overrides the admin explicitly set, and
  // let the global CSS (font-heading, h1Size, headingHex, etc.)
  // fill in the rest.
  const titleInline = {
    fontFamily: ts.fontFamily || undefined,
    fontSize: ts.fontSize ? resolveFontSize(ts.fontSize) : undefined,
    fontWeight: ts.fontWeight || undefined,
    color: ts.color || undefined,
    textAlign: ts.textAlign || undefined,
  };

  return (
    <PageShell>
      <div className="min-h-[calc(100vh-theme(spacing.16)-theme(spacing.64))]">
        <div className="slh-page-placeholder px-4 py-12">
          <article>
            <h1
              data-testid="custom-page-title"
              className="mb-8"
              style={titleInline}
            >
              {page.title}
            </h1>
            <RichHtml
              className="custom-page-content"
              html={page.content}
            />
            <style>{`
              /* Match Tiptap editor WYSIWYG styles, but pull live from
                 Site Styles' CSS vars (set on :root) so a published custom
                 page's typography always matches the current admin config. */
              .custom-page-content {
                font-size: var(--base-font-size, 16px);
                font-weight: var(--body-weight, 400);
                line-height: var(--body-line-height, 1.6);
                color: #333;
                font-family: var(--font-body, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif);
              }

              /* Typography - Match editor rendering */
              .custom-page-content h1 {
                font-family: var(--font-heading, inherit);
                font-size: var(--h1-size, 2.5rem);
                font-weight: var(--heading-weight, 700);
                line-height: var(--heading-line-height, 1.2);
                margin-top: 1.5rem;
                margin-bottom: 1rem;
                color: #111;
              }
              .custom-page-content h2 {
                font-family: var(--font-heading, inherit);
                font-size: var(--h2-size, 2rem);
                font-weight: var(--heading-weight, 600);
                line-height: var(--heading-line-height, 1.3);
                margin-top: 1.5rem;
                margin-bottom: 0.875rem;
                color: #111;
              }
              .custom-page-content h3 {
                font-family: var(--font-heading, inherit);
                font-size: var(--h3-size, 1.5rem);
                font-weight: var(--heading-weight, 600);
                line-height: var(--heading-line-height, 1.4);
                margin-top: 1.25rem;
                margin-bottom: 0.75rem;
                color: #111;
              }
              .custom-page-content p {
                margin-top: 0.75rem;
                margin-bottom: 0.75rem;
                font-size: var(--base-font-size, 16px);
                line-height: var(--body-line-height, 1.6);
              }
              .custom-page-content strong {
                font-weight: 700;
                color: #111;
              }
              .custom-page-content em {
                font-style: italic;
              }
              .custom-page-content u {
                text-decoration: underline;
              }
              .custom-page-content s {
                text-decoration: line-through;
              }
              .custom-page-content code {
                background: #f3f4f6;
                padding: 0.2em 0.4em;
                border-radius: 3px;
                font-size: 0.9em;
                font-family: 'Courier New', monospace;
                color: #e83e8c;
              }

              /* Lists */
              .custom-page-content ul,
              .custom-page-content ol {
                padding-left: 2rem;
                margin: 1rem 0;
              }
              .custom-page-content li {
                margin: 0.5rem 0;
              }
              /* Same li>p specificity fix as TiptapEditor — otherwise the
                 ".custom-page-content p" rule above always wins over List
                 Font Size on the public page. */
              .custom-page-content li p {
                font-size: inherit;
                font-family: inherit;
              }
              .custom-page-content ul {
                list-style-type: disc;
              }
              .custom-page-content ol {
                list-style-type: decimal;
              }

              /* Blockquote */
              .custom-page-content blockquote {
                border-left: 4px solid #e5e7eb;
                padding-left: 1rem;
                margin: 1.5rem 0;
                color: #6b7280;
                font-style: italic;
              }

              /* Images */
              .custom-page-content img {
                max-width: 100%;
                height: auto;
                border-radius: 8px;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                margin: 1.5rem 0;
                display: block;
              }

              /* Links */
              .custom-page-content a {
                color: #2563eb;
                text-decoration: underline;
              }
              .custom-page-content a:hover {
                color: #1d4ed8;
              }

              /* Code blocks */
              .custom-page-content pre {
                background: #1e1e1e;
                color: #d4d4d4;
                font-family: 'Courier New', monospace;
                padding: 1rem;
                border-radius: 8px;
                margin: 1.5rem 0;
                overflow-x: auto;
              }
              .custom-page-content pre code {
                background: none;
                color: inherit;
                font-size: 0.875rem;
                padding: 0;
              }

              /* Video embeds */
              .custom-page-content iframe {
                max-width: 100%;
                display: block;
                margin: 2rem auto;
                border-radius: 8px;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              }
              .custom-page-content div[data-youtube-video] iframe {
                width: 100%;
                height: 360px;
                border: none;
                border-radius: 8px;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
              }

              /* Text alignment */
              .custom-page-content [style*="text-align: center"] {
                text-align: center;
              }
              .custom-page-content [style*="text-align: right"] {
                text-align: right;
              }
              .custom-page-content [style*="text-align: justify"] {
                text-align: justify;
              }
            `}</style>
          </article>
        </div>
      </div>
    </PageShell>
  );
}
